// Wan 3.0: Beijing Bailian API. Input and output video seconds are both billed.
export const WAN_MODEL_IDS = {
  'wan-3.0': 'wan3.0-video',
  'wan-3.0-prime': 'wan3.0-video-prime',
};
export const isWanVideoModel = (key) => Object.hasOwn(WAN_MODEL_IDS, key || '');
export const WAN_VIDEO_SIZES = {
  '16:9': { '480p': '854x480', '720p': '1280x720', '1080p': '1920x1080' },
  '4:3': { '480p': '640x480', '720p': '960x720', '1080p': '1440x1080' },
  '1:1': { '480p': '480x480', '720p': '720x720', '1080p': '1080x1080' },
  '3:4': { '480p': '480x640', '720p': '720x960', '1080p': '1080x1440' },
  '9:16': { '480p': '480x854', '720p': '720x1280', '1080p': '1080x1920' },
  adaptive: { '480p': 'adaptive_480p', '720p': 'adaptive_720p', '1080p': 'adaptive_1080p' },
};
export const WAN_VIDEO_CAPABILITIES = {
  allowedAspectRatios: Object.keys(WAN_VIDEO_SIZES),
  allowedResolutions: ['480p', '720p', '1080p'],
  allowedSizesByAspect: WAN_VIDEO_SIZES,
  allowedDurations: Array.from({ length: 29 }, (_, i) => i + 2),
  defaultDuration: 5,
  allowedInputModes: ['text', 'image', 'video', 'audio'],
  allowedVideoFunctionModes: ['general', 'reference', 'frames', 'editing', 'extend'],
};
export const getWanInputVideoDuration = (assets = []) => assets
  .filter((asset) => asset?.type === 'video')
  .reduce((total, asset) => total + Math.max(0, Number(asset.durationSeconds) || 0), 0);

export const resolveWanVideoSize = (size) => {
  for (const [ratio, sizes] of Object.entries(WAN_VIDEO_SIZES)) {
    for (const [label, value] of Object.entries(sizes)) {
      if (value === size) return { ratio, resolution: label.toUpperCase() };
    }
  }
  return null;
};
export const getWanVideoCreditCost = ({ modelKey, duration = 5, inputVideoDuration = 0, resolutionLabel, resolution, size } = {}) => {
  const label = String(resolutionLabel || resolveWanVideoSize(size || resolution)?.resolution || '720p').toLowerCase();
  const rate = (modelKey === 'wan-3.0-prime' ? { '480p': 30, '720p': 60, '1080p': 120 } : { '480p': 12, '720p': 24, '1080p': 48 })[label];
  if (!rate) throw new Error('Unsupported Wan video resolution.');
  // Match the existing Seedance / MiniMax retail billing rule: whole seconds.
  return (Math.round(Number(duration) || 5) + Math.round(Math.max(0, Number(inputVideoDuration) || 0))) * rate;
};
export const validateWanVideoInputs = (assets = [], { duration } = {}) => {
  for (const asset of assets) {
    const timed = ['video', 'audio'].includes(asset.type);
    const seconds = Number(asset.durationSeconds);
    if (timed && (!Number.isFinite(seconds) || seconds < 1 || seconds > 15)) {
      return { code: 'duration', message: `Wan reference ${asset.type} must be 1–15 seconds long.` };
    }
    const maxMb = asset.type === 'video' ? 100 : asset.type === 'audio' ? 15 : 20;
    if (Number(asset.fileSizeBytes || asset.sizeBytes || asset.fileSize) > maxMb * 1024 * 1024) {
      return { code: 'file_size', message: `Wan ${asset.type} files must be at most ${maxMb} MB.` };
    }
    if (asset.type !== 'audio' && Number(asset.width) > 0 && Number(asset.height) > 0) {
      const short = Math.min(asset.width, asset.height);
      const long = Math.max(asset.width, asset.height);
      if (short < 240 || long > (asset.type === 'video' ? 4096 : 8000) || long / short > 8) {
        return { code: 'dimensions', message: 'Wan input dimensions or aspect ratio are outside the supported range.' };
      }
    }
    if (asset.type === 'video' && Number(asset.fps) > 0 && Number(asset.fps) < 16) {
      return { code: 'fps', message: 'Wan input videos require at least 16 fps.' };
    }
  }
  for (const type of ['video', 'audio']) {
    const seconds = assets.filter((asset) => asset.type === type).reduce((sum, asset) => sum + Number(asset.durationSeconds), 0);
    if (seconds > 15) return { code: 'total_duration', message: `Wan reference ${type} can total up to 15 seconds.` };
  }
  if (duration != null && (!Number.isInteger(Number(duration)) || duration < 2 || duration > 30 || getWanInputVideoDuration(assets) + Number(duration) > 30)) {
    return { code: 'output_duration', message: 'Wan output must be 2–30 seconds, with input and output video totaling at most 30 seconds.' };
  }
  return null;
};

// Persist provider-reported seconds for cost reconciliation, independently of
// the retail estimate stored with the original request.
export const getWanProviderUsage = (provider, data) => {
  if (provider?.type !== 'Bailian_Video' || !Object.values(WAN_MODEL_IDS).includes(provider?.model)) return null;
  const usage = data?.usage;
  const inputSeconds = Number(usage?.input_video_duration ?? 0);
  const outputSeconds = Number(usage?.output_video_duration ?? (Number(usage?.duration) - inputSeconds));
  if (!Number.isFinite(outputSeconds) || outputSeconds <= 0 || !Number.isFinite(inputSeconds) || inputSeconds < 0) return null;
  return { inputVideoSeconds: inputSeconds, outputVideoSeconds: outputSeconds, billableVideoSeconds: inputSeconds + outputSeconds, resolution: usage.SR, fps: usage.fps, ratio: usage.ratio };
};
